import { useMemo } from 'react';

import InputAdornment from '@mui/material/InputAdornment';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Link from '@mui/material/Link';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import MenuItem from '@mui/material/MenuItem';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import ToggleButton from '@mui/material/ToggleButton';

import { Icon } from '@/components/atoms/Icon';

export type CareType = 'careHome' | 'homeCare';

export type Filter = { id: string; label: string; options: string[] };

const CARE_HOME_FILTERS: Filter[] = [
  {
    id: 'careProvided',
    label: 'Care provided',
    options: ['Residential', 'Nursing', 'Dementia', 'Palliative'],
  },
  {
    id: 'funding',
    label: 'Funding type',
    options: ['Self-funded', 'Local authority', 'NHS'],
  },
  {
    id: 'distance',
    label: 'Distance',
    options: [
      'Within 5 miles',
      'Within 10 miles',
      'Within 25 miles',
      'Within 50 miles',
    ],
  },
  {
    id: 'rating',
    label: 'Rating',
    options: ['Outstanding', 'Good'],
  },
];

const HOME_CARE_FILTERS: Filter[] = [
  {
    id: 'distance',
    label: 'Distance',
    options: [
      'Within 5 miles',
      'Within 10 miles',
      'Within 25 miles',
      'Within 50 miles',
    ],
  },
  {
    id: 'hourlyRate',
    label: 'Hourly rate',
    options: ['Up to £15', '£15 - £20', '£20 - £25', '£25 or more'],
  },
  {
    id: 'visitDuration',
    label: 'Visit duration',
    options: ['60 minutes', '45 minutes', '30 minutes', '15 minutes'],
  },
  {
    id: 'rating',
    label: 'Rating',
    options: ['Outstanding', 'Good'],
  },
];

export const getFiltersForCareType = (careType: CareType): Filter[] =>
  careType === 'homeCare' ? HOME_CARE_FILTERS : CARE_HOME_FILTERS;

interface FilterOptionProps {
  id: string;
  label: string;
  value: string;
  setValue: (val: string) => void;
  options: string[];
}
const FilterOption = ({
  id,
  label,
  value,
  setValue,
  options,
}: FilterOptionProps) => (
  <TextField
    select
    size='xsmall'
    fullWidth
    value={value}
    onChange={(e) => setValue(e.target.value)}
    slotProps={{
      select: {
        displayEmpty: true,
        renderValue: () =>
          value ? (id === 'rating' ? `Rating ${value}` : value) : label,
      },
    }}
  >
    <MenuItem value=''>
      <em>Any</em>
    </MenuItem>
    {options.map((option) => (
      <MenuItem key={option} value={option}>
        {option}
      </MenuItem>
    ))}
  </TextField>
);

interface CareSearchFiltersProps {
  careType: CareType;
  onCareTypeChange: (careType: CareType) => void;
  filters: Record<string, string>;
  location: string;
  setLocation: (val: string) => void;
  setFilter: (id: string, value: string) => void;
  clearFilters: () => void;
  onSearch?: (location: string) => void;
}

export const CareSearchFilters = ({
  careType,
  onCareTypeChange,
  filters,
  setFilter,
  clearFilters,
  location,
  setLocation,
  onSearch,
}: CareSearchFiltersProps) => {
  const visibleFilters = useMemo(
    () => getFiltersForCareType(careType),
    [careType],
  );

  const activeCount = useMemo(
    () => visibleFilters.filter((f) => filters[f.id]).length,
    [filters, visibleFilters],
  );

  return (
    <Stack spacing={1}>
      {/* Same grid as the filters below, so the search field can span exactly
          two filter columns plus the gap between them and stay aligned as the
          columns flex. */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))',
          gap: 1.5,
          alignItems: 'center',
        }}
      >
        <Box
          sx={{
            gridColumn: { xs: '1 / -1', sm: 'span 2' },
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
            minWidth: 0,
          }}
        >
          <TextField
            fullWidth
            size='xsmall'
            placeholder='Search by name or location'
            value={location}
            onChange={(e) => setLocation(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && onSearch) {
                onSearch(location);
              }
            }}
            slotProps={{
              input: {
                startAdornment: (
                  <InputAdornment position='start'>
                    <Icon icon='magnifying-glass' fontSize={16} />
                  </InputAdornment>
                ),
                style: { borderRadius: 9999 },
              },
            }}
          />
          <Button
            variant='contained'
            size='small'
            onClick={() => onSearch?.(location)}
          >
            Search
          </Button>
        </Box>
        <Box
          sx={{
            gridColumn: { xs: '1 / -1', sm: '3 / -1' },
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
            minWidth: 0,
          }}
        >
          <ToggleButtonGroup
            size='xsmall'
            exclusive
            value={careType}
            onChange={(_, val) => val && onCareTypeChange(val as CareType)}
            sx={{
              flex: { xs: 1, sm: 'none' },
              '& .MuiToggleButton-root': { flex: { xs: 1, sm: 'none' } },
            }}
          >
            <ToggleButton value='careHome'>Care home</ToggleButton>
            <ToggleButton value='homeCare'>Home care</ToggleButton>
          </ToggleButtonGroup>
          {activeCount > 0 && (
            <Link
              component='button'
              type='button'
              variant='body2'
              onClick={clearFilters}
            >
              Clear all
            </Link>
          )}
        </Box>
      </Box>

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))',
          gap: 1.5,
        }}
      >
        {visibleFilters.map((filter) => (
          <FilterOption
            key={filter.id}
            id={filter.id}
            label={filter.label}
            value={filters[filter.id] ?? ''}
            setValue={(val: string) => setFilter(filter.id, val)}
            options={filter.options}
          />
        ))}
      </Box>
    </Stack>
  );
};

export default CareSearchFilters;
